import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import {
  List as ListIcon,
  Layers,
  Heart,
  Check,
  X,
  Ban,
  Loader2,
  MessageCircle,
  Mail,
  Phone,
  Video,
  Database,
  Sparkles,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_META = {
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: 'text-emerald-600' },
  email:    { icon: Mail,          label: 'Email',    color: 'text-sky-600' },
  voice:    { icon: Phone,         label: 'Voix',     color: 'text-violet-600' },
  meeting:  { icon: Video,         label: 'Visio',    color: 'text-amber-600' },
  crm:      { icon: Database,      label: 'CRM',      color: 'text-slate-600' },
};

const CATEGORY_META = {
  pro:        { label: 'Pro',         color: 'bg-blue-100 text-blue-800 border-blue-200' },
  perso:      { label: 'Personnel',   color: 'bg-rose-100 text-rose-800 border-rose-200' },
  spam:       { label: 'Spam',        color: 'bg-slate-100 text-slate-700 border-slate-200' },
  demarchage: { label: 'Démarchage',  color: 'bg-amber-100 text-amber-800 border-amber-200' },
  inconnu:    { label: 'Inconnu',     color: 'bg-muted text-muted-foreground border-border' },
};

function CategoryBadge({ suggestion }) {
  const cat = suggestion?.category || 'inconnu';
  const meta = CATEGORY_META[cat] || CATEGORY_META.inconnu;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${meta.color}`}
      title={suggestion?.reasoning || ''}
    >
      <Sparkles className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function ChannelIcon({ channel }) {
  const meta = CHANNEL_META[channel] || CHANNEL_META.whatsapp;
  const Icon = meta.icon;
  return <Icon className={`w-4 h-4 ${meta.color}`} title={meta.label} />;
}


// ─── MODE 1 — Liste (desktop default) ────────────────────────────────────


function ListMode({ items, onDecide, decidingIds }) {
  const [selected, setSelected] = useState(new Set());

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allChecked = items.length > 0 && selected.size === items.length;

  const bulk = async (decision) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    await onDecide(ids.map((id) => ({ request_id: id, decision })));
    setSelected(new Set());
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground" data-testid="privacy-validation-empty">
        <Check className="w-10 h-10 mx-auto mb-2 text-[#16A34A]" />
        <p className="font-medium text-foreground">Tout est trié !</p>
        <p className="text-xs mt-1">Aucune conversation en attente de validation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="privacy-validation-list">
      {/* Bulk bar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 rounded-md">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={() => setSelected(allChecked ? new Set() : new Set(items.map(i => i.id)))}
            data-testid="privacy-validation-checkall"
          />
          <span className="text-muted-foreground">
            {selected.size > 0 ? `${selected.size} sélectionné(s)` : `${items.length} en attente`}
          </span>
        </label>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={selected.size === 0}
                  onClick={() => bulk('accept')} className="text-[#16A34A] border-[#16A34A]/40"
                  data-testid="privacy-validation-bulk-accept">
            <Check className="w-4 h-4 mr-1" /> Ingérer
          </Button>
          <Button size="sm" variant="outline" disabled={selected.size === 0}
                  onClick={() => bulk('reject')} className="text-[#DC2626] border-[#DC2626]/40"
                  data-testid="privacy-validation-bulk-reject">
            <X className="w-4 h-4 mr-1" /> Privé
          </Button>
        </div>
      </div>

      {/* Rows */}
      <div className="border rounded-lg bg-card max-h-[55vh] overflow-y-auto">
        {items.map((r) => {
          const busy = decidingIds.has(r.id);
          const lastMsg = (r.message_previews || [])[r.message_previews?.length - 1];
          return (
            <div
              key={r.id}
              className={`flex items-center gap-3 px-3 py-3 border-b last:border-b-0 hover:bg-muted/40 ${selected.has(r.id) ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`}
              data-testid={`privacy-validation-row-${r.id}`}
            >
              <input
                type="checkbox"
                checked={selected.has(r.id)}
                onChange={() => toggle(r.id)}
                className="shrink-0"
              />
              <ChannelIcon channel={r.channel} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">{r.contact_name}</span>
                  <CategoryBadge suggestion={r.ai_suggestion} />
                  <span className="text-[10px] text-muted-foreground">
                    {r.message_count} msg
                  </span>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {lastMsg?.text || '(sans aperçu)'}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" disabled={busy}
                        onClick={() => onDecide([{ request_id: r.id, decision: 'accept' }])}
                        className="text-[#16A34A]"
                        data-testid={`privacy-validation-accept-${r.id}`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button size="icon" variant="ghost" disabled={busy}
                        onClick={() => onDecide([{ request_id: r.id, decision: 'reject' }])}
                        className="text-[#DC2626]"
                        data-testid={`privacy-validation-reject-${r.id}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── MODE 2 — Carte (détaillée) ──────────────────────────────────────────


function CardMode({ items, onDecide, decidingIds }) {
  const [idx, setIdx] = useState(0);
  const safeIdx = Math.min(idx, Math.max(0, items.length - 1));
  const r = items[safeIdx];

  useEffect(() => { setIdx(0); }, [items.length === 0]);

  if (!r) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        <Check className="w-10 h-10 mx-auto mb-2 text-[#16A34A]" />
        <p className="font-medium text-foreground">Tout est trié !</p>
      </div>
    );
  }

  const next = () => setIdx((i) => Math.min(items.length - 1, i + 1));
  const prev = () => setIdx((i) => Math.max(0, i - 1));
  const decide = async (decision) => {
    await onDecide([{ request_id: r.id, decision }]);
    // Move to next if any remain
    if (idx >= items.length - 1) setIdx(Math.max(0, items.length - 2));
  };
  const busy = decidingIds.has(r.id);

  return (
    <Card className="border-border/60" data-testid="privacy-validation-card-mode">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChannelIcon channel={r.channel} />
            <span className="text-sm font-medium">{r.contact_name}</span>
            <CategoryBadge suggestion={r.ai_suggestion} />
          </div>
          <div className="text-xs text-muted-foreground">
            {safeIdx + 1} / {items.length}
          </div>
        </div>
        {r.ai_suggestion?.reasoning && (
          <div className="text-xs italic text-muted-foreground bg-muted/40 rounded p-2">
            💡 {r.ai_suggestion.reasoning}
          </div>
        )}

        <div className="space-y-2 max-h-[35vh] overflow-y-auto">
          {(r.message_previews || []).map((m, i) => (
            <div key={i} className="text-sm bg-muted/30 rounded p-3">
              <div className="text-[10px] text-muted-foreground mb-1">
                {new Date(m.ts).toLocaleString('fr-FR')}
              </div>
              <div>{m.text}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2">
          <Button variant="outline" disabled={busy}
                  className="text-[#DC2626] border-[#DC2626]/40"
                  onClick={() => decide('reject')}
                  data-testid="privacy-validation-card-reject">
            <X className="w-4 h-4 mr-2" /> Privé
          </Button>
          <Button disabled={busy}
                  className="bg-[#16A34A] hover:bg-[#15803D]"
                  onClick={() => decide('accept')}
                  data-testid="privacy-validation-card-accept">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Ingérer
          </Button>
          <Button variant="outline" disabled={busy}
                  className="text-slate-700 border-slate-300"
                  onClick={() => decide('block')}
                  data-testid="privacy-validation-card-block">
            <Ban className="w-4 h-4 mr-2" /> Bloquer
          </Button>
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <Button size="sm" variant="ghost" onClick={prev} disabled={safeIdx === 0}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </Button>
          <Button size="sm" variant="ghost" onClick={next} disabled={safeIdx === items.length - 1}>
            Suivant <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ─── MODE 3 — Swipe (Tinder) ─────────────────────────────────────────────


function SwipeMode({ items, onDecide, decidingIds }) {
  const [idx, setIdx] = useState(0);
  const [exiting, setExiting] = useState(null); // 'left' | 'right' | 'up'
  const r = items[idx];

  useEffect(() => { setIdx(0); }, [items.length === 0]);

  if (!r) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        <Heart className="w-10 h-10 mx-auto mb-2 text-pink-500" />
        <p className="font-medium text-foreground">Plus rien à trier 🎉</p>
      </div>
    );
  }

  const decide = async (direction) => {
    const decision = direction === 'right' ? 'accept' : direction === 'up' ? 'block' : 'reject';
    setExiting(direction);
    await onDecide([{ request_id: r.id, decision }]);
    setTimeout(() => {
      setExiting(null);
      setIdx((i) => Math.min(items.length - 1, i + 1));
    }, 240);
  };

  const handleDrag = (event, info) => {
    const { offset, velocity } = info;
    if (offset.y < -120 || velocity.y < -500) return decide('up');
    if (offset.x > 120 || velocity.x > 500) return decide('right');
    if (offset.x < -120 || velocity.x < -500) return decide('left');
  };

  return (
    <div className="relative h-[60vh] flex flex-col items-center justify-center select-none"
         data-testid="privacy-validation-swipe-mode">
      <div className="text-xs text-muted-foreground mb-3">
        {idx + 1} / {items.length}  ·  ← Privé  ·  ↑ Bloquer  ·  → Ingérer
      </div>

      <div className="relative w-full max-w-md h-[42vh]">
        <AnimatePresence>
          <motion.div
            key={r.id}
            drag
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            dragElastic={0.7}
            onDragEnd={handleDrag}
            initial={{ scale: 0.95, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={
              exiting === 'right' ? { x: 600, rotate: 20, opacity: 0 } :
              exiting === 'left'  ? { x: -600, rotate: -20, opacity: 0 } :
              exiting === 'up'    ? { y: -600, opacity: 0 } :
              { opacity: 0 }
            }
            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
            className="absolute inset-0 bg-card border-2 rounded-2xl shadow-xl cursor-grab active:cursor-grabbing p-6 flex flex-col"
          >
            <div className="flex items-center gap-2 mb-2">
              <ChannelIcon channel={r.channel} />
              <span className="text-sm font-medium">{r.contact_name}</span>
              <CategoryBadge suggestion={r.ai_suggestion} />
            </div>
            {r.ai_suggestion?.reasoning && (
              <div className="text-xs italic text-muted-foreground mb-2">
                💡 {r.ai_suggestion.reasoning}
              </div>
            )}
            <div className="flex-1 overflow-y-auto space-y-2">
              {(r.message_previews || []).slice(-5).map((m, i) => (
                <div key={i} className="text-sm bg-muted/40 rounded p-2">
                  {m.text}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground mt-3 text-center">
              👆 Glisse →  Ingère   ←  Privé   ↑  Bloque
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <Button size="icon" variant="outline" onClick={() => decide('left')}
                disabled={decidingIds.has(r.id)}
                className="w-14 h-14 rounded-full border-[#DC2626]/40 text-[#DC2626]"
                data-testid="privacy-swipe-btn-reject">
          <X className="w-6 h-6" />
        </Button>
        <Button size="icon" variant="outline" onClick={() => decide('up')}
                disabled={decidingIds.has(r.id)}
                className="w-12 h-12 rounded-full border-slate-300"
                data-testid="privacy-swipe-btn-block">
          <Ban className="w-5 h-5" />
        </Button>
        <Button size="icon" onClick={() => decide('right')}
                disabled={decidingIds.has(r.id)}
                className="w-14 h-14 rounded-full bg-[#16A34A] hover:bg-[#15803D]"
                data-testid="privacy-swipe-btn-accept">
          <Check className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
}


// ─── Main tab — switches between modes + settings ─────────────────────────


export function ValidationTab({ token, refreshStats }) {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | card | swipe
  const [decidingIds, setDecidingIds] = useState(new Set());

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [pendingRes, settingsRes] = await Promise.all([
        axios.get(`${API}/privacy/pending?status=pending&limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/privacy/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setItems(pendingRes.data?.items || []);
      setSettings(settingsRes.data);
    } catch (e) {
      toast.error('Impossible de charger la file de validation');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDecide = useCallback(async (decisions) => {
    const ids = decisions.map((d) => d.request_id);
    setDecidingIds((prev) => new Set([...prev, ...ids]));
    try {
      const res = await axios.post(
        `${API}/privacy/decisions`,
        { decisions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const applied = res.data?.applied || 0;
      toast.success(`${applied} décision(s) appliquée(s)`);
      // Remove from local list
      setItems((prev) => prev.filter((r) => !ids.includes(r.id)));
      refreshStats?.();
    } catch (e) {
      toast.error('Erreur lors de l’application des décisions');
    } finally {
      setDecidingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [token, refreshStats]);

  const updateMode = async (newMode) => {
    try {
      const res = await axios.patch(
        `${API}/privacy/settings`,
        { ingestion_validation_mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings((prev) => ({ ...(prev || {}), ingestion_validation_mode: res.data?.ingestion_validation_mode }));
      toast.success('Mode de validation mis à jour');
    } catch (e) {
      toast.error('Permission refusée');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
      </div>
    );
  }

  const currentMode = settings?.ingestion_validation_mode || 'disabled';

  return (
    <div className="space-y-4" data-testid="privacy-validation-tab">
      {/* Mode setting strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-muted/40 rounded-md">
        <div className="flex items-center gap-2 text-xs">
          <SettingsIcon className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground uppercase tracking-wider">Mode</span>
          <div className="flex gap-1 ml-1">
            {[
              { k: 'disabled', label: 'Désactivé' },
              { k: 'smart',    label: 'Smart' },
              { k: 'strict',   label: 'Strict' },
              { k: 'weekly',   label: 'Hebdo' },
            ].map((m) => (
              <Button
                key={m.k}
                size="sm"
                variant={currentMode === m.k ? 'default' : 'outline'}
                onClick={() => updateMode(m.k)}
                data-testid={`privacy-mode-${m.k}`}
              >
                {m.label}
              </Button>
            ))}
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1">
          <Button size="sm" variant={mode === 'list' ? 'default' : 'outline'}
                  onClick={() => setMode('list')} data-testid="privacy-view-list">
            <ListIcon className="w-4 h-4" />
          </Button>
          <Button size="sm" variant={mode === 'card' ? 'default' : 'outline'}
                  onClick={() => setMode('card')} data-testid="privacy-view-card">
            <Layers className="w-4 h-4" />
          </Button>
          <Button size="sm" variant={mode === 'swipe' ? 'default' : 'outline'}
                  onClick={() => setMode('swipe')} data-testid="privacy-view-swipe">
            <Heart className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {currentMode === 'disabled' && (
        <div className="text-center py-12 text-sm text-muted-foreground border rounded-lg bg-muted/30">
          <SettingsIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="font-medium text-foreground">Mode de validation désactivé</p>
          <p className="text-xs mt-1 max-w-md mx-auto">
            Active le mode <b>Smart</b> pour que les conversations d’inconnus
            soient mises en attente avant ingestion (analyse IA pro / perso / spam incluse).
          </p>
        </div>
      )}

      {currentMode !== 'disabled' && mode === 'list' && (
        <ListMode items={items} onDecide={handleDecide} decidingIds={decidingIds} />
      )}
      {currentMode !== 'disabled' && mode === 'card' && (
        <CardMode items={items} onDecide={handleDecide} decidingIds={decidingIds} />
      )}
      {currentMode !== 'disabled' && mode === 'swipe' && (
        <SwipeMode items={items} onDecide={handleDecide} decidingIds={decidingIds} />
      )}
    </div>
  );
}

export default ValidationTab;
